import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import React, { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { LinearGaugeWithSubtitle } from '../../../commons/LinearGaugeWithSubtitle';
import ExpandableCard from '../../../commons/ExpandableCard';
import Button from '@amzn/meridian/button';
import UserBadgeThumbnails from '../../Dashboard/DashboardComponents/UserBadgeThumbnails';
import { MAX_THUMBNAIL_DISPLAY } from '../types/constants';
import AssociatesRequiredReviewsCard from './AssociatesRequiredReviewsCard';

import {
  calculateAssociatesData,
  calculateMetricsData,
  getOutlierI18nKey,
} from '../helpers';
import {
  FilterValues,
  OutlierMetricsData,
  AssociatesMetricsData,
  AssociateDetails,
  OutlierCategory,
} from '../types/types';
import { StateContext } from '../../../GlobalStateStore';

interface OutlierMetricsCardProps extends OutlierMetricsData {
  onFilterChange: (newFilters: Partial<FilterValues>) => void;
}

interface AssociatesCardProps extends AssociatesMetricsData {
  onFilterChange: (newFilters: Partial<FilterValues>) => void;
  associatesWithOutliers: AssociateDetails[];
}

interface OutlierCardsColumnProps {
  onFilterChange: (newFilters: Partial<FilterValues>) => void;
  outlierCategories: OutlierCategory[];
  associatesWithOutliers: AssociateDetails[];
  /**
   * Set of valid CSA logins to filter required reviews by.
   * Only contacts for CSAs in this set will be counted toward required reviews.
   * This is used to exclude CSAs who no longer report to the manager.
   */
  validCSALogins: Set<string>;
}

const OutlierMetricsCard: React.FC<OutlierMetricsCardProps> = ({
  pendingOutliers,
  reviewedCount,
  totalCount,
  pendingOutlierTypes,
  reviewMetrics,
  requiredReviews,
  onFilterChange,
}) => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;

  const title = (
    <FormattedMessage
      id='cs_perfportal_pending_outliers_this_week'
      defaultMessage='Outlier reviews this week'
    />
  );

  const header = (
    <Row spacingInset='400 none 100 300'>
      <Column spacing='tiny'>
        <Row spacing='tiny'>
          <Text type='h400'>{requiredReviews.remainingRequired}</Text>
        </Row>
        <Row spacing='none'>
          <Text type='h50'>
            <FormattedMessage
              id='cs_perfportal_remaining_required'
              defaultMessage='Remaining required'
            />
          </Text>
        </Row>
      </Column>
      <Column spacing='large' spacingInset='400 300 none 400'>
        <LinearGaugeWithSubtitle
          total={requiredReviews.totalRequired}
          value={requiredReviews.completedRequired}
          width={175}
          subtitle={
            <FormattedMessage
              id='cs_perfportal_required_completed_count'
              defaultMessage='{completed}/{total} required completed'
              values={{
                completed: requiredReviews.completedRequired,
                total: requiredReviews.totalRequired,
              }}
            />
          }
        />
      </Column>
    </Row>
  );

  const expandedContent = (
    <Column spacing='small' spacingInset='none 200 none none'>
      {pendingOutlierTypes.map((outlier, index) => (
        <Row
          key={index}
          alignmentHorizontal='justify'
          alignmentVertical='center'
          spacing='small'
          width='100%'
        >
          <Row
            alignmentVertical='center'
            spacing='small'
            spacingInset='200 none'
          >
            <Column>
              <Text type='h400'>
                {outlier.requiredReviewsRemaining || outlier.count}
              </Text>
            </Column>
            <Column
              spacingInset='300 none 100 200'
              spacing='none'
              alignmentVertical='bottom'
            >
              <Text type='b200'>
                <FormattedMessage
                  id={getOutlierI18nKey(outlier.type)}
                  defaultMessage={outlier.type}
                />
              </Text>
            </Column>
          </Row>
          {!outlier.type.toLowerCase().includes('other') && (
            <Row spacingInset='none none none none'>
              <Button
                onClick={() =>
                  onFilterChange({
                    category: outlier.type,
                  })
                }
                size='small'
                type='secondary'
                data-csa-c-type='button'
                data-csa-c-slot-id='tm-outlier-management-pending-outlier-filter-button-slot'
                data-csa-c-tag1='Coaching'
                data-csa-c-tag2='Outlier Metrics'
                data-csa-c-tag3={outlier.type}
                data-csa-c-profile-id={requesterUserID}
                data-csa-c-username={requesterUsername}
                data-csa-c-is-TM={isTm}
                data-csa-c-is-OM={isOm}
              >
                <FormattedMessage
                  id='cs_perfportal_filter_by'
                  defaultMessage='Filter by'
                />
              </Button>
            </Row>
          )}
        </Row>
      ))}
    </Column>
  );

  return (
    <ExpandableCard
      title={title}
      header={header}
      expandedContent={expandedContent}
      cardSpacingInset='450 400 300 450'
      clickstreamTags={{
        'data-csa-c-type': 'link',
        'data-csa-c-slot-id': `tm-outlier-management-pending-outlier-link-slot`,
        'data-csa-c-tag1': 'Coaching',
        'data-csa-c-tag2': 'Outlier Metrics',
        'data-csa-c-tag3': `Pending Outlier`,
        'data-csa-c-profile-id': requesterUserID,
        'data-csa-c-username': requesterUsername,
        'data-csa-c-is-TM': isTm,
        'data-csa-c-is-OM': isOm,
      }}
    />
  );
};

const AssociatesCard: React.FC<AssociatesCardProps> = ({
  totalAssociates,
  pendingAssociateTypes,
  associatesWithOutliers,
  onFilterChange,
}) => {
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, isOm, requesterUserID, requesterUsername } =
    inspireOptions.data;
  const title = (
    <FormattedMessage
      id='cs_perfportal_associates_with_outliers'
      defaultMessage='Associates with outliers'
    />
  );

  const associateLogins = associatesWithOutliers.map(
    associate => associate.login
  );

  const header = (
    <Row
      spacingInset='500 200 200 none'
      alignmentHorizontal='justify'
      alignmentVertical='center'
      width='fill'
    >
      <Row spacing='300' alignmentVertical='center'>
        <Text type='h400'>{totalAssociates}</Text>
        <Text type='h50'>
          <FormattedMessage
            id='cs_perfportal_associates'
            defaultMessage='Associates'
          />
        </Text>
      </Row>
      <Column spacingInset='none 400 300 none'>
        <UserBadgeThumbnails
          usernames={associateLogins}
          maxThumbnailNumber={MAX_THUMBNAIL_DISPLAY}
        />
      </Column>
    </Row>
  );

  const expandedContent = (
    <Column spacing='small' width='fill'>
      <Text type='b200' color='secondary'>
        <FormattedMessage
          id='cs_perfportal_categories_most_associates'
          defaultMessage='Categories with the most Associates'
        />
      </Text>
      {pendingAssociateTypes.map((type, index) => (
        <Row
          key={index}
          alignmentHorizontal='justify'
          alignmentVertical='center'
          spacing='small'
          width='fill'
        >
          <Row
            alignmentVertical='center'
            spacing='small'
            spacingInset='200 none'
            width='fill'
          >
            <Column>
              <Text type='h400'>{type.count}</Text>
            </Column>
            <Column
              spacingInset='300 none none 200'
              alignmentVertical='bottom'
              width='fill'
            >
              <Text type='b200'>
                <FormattedMessage
                  id={getOutlierI18nKey(type.type)}
                  defaultMessage={type.type}
                />
              </Text>
            </Column>
          </Row>

          {!type.type.toLowerCase().includes('other') && (
            <Row spacingInset='none 200 none none'>
              <Button
                onClick={() =>
                  onFilterChange({
                    category: type.type,
                  })
                }
                size='small'
                type='secondary'
                data-csa-c-type='button'
                data-csa-c-slot-id='tm-outlier-management-associate-outlier-filter-button-slot'
                data-csa-c-tag1='Coaching'
                data-csa-c-tag2='Outlier Metrics'
                data-csa-c-tag3={type.type}
                data-csa-c-profile-id={requesterUserID}
                data-csa-c-username={requesterUsername}
                data-csa-c-is-TM={isTm}
                data-csa-c-is-OM={isOm}
              >
                <FormattedMessage
                  id='cs_perfportal_filter_by'
                  defaultMessage='Filter by'
                />
              </Button>
            </Row>
          )}
        </Row>
      ))}
    </Column>
  );
  return (
    <ExpandableCard
      title={title}
      header={header}
      expandedContent={expandedContent}
      cardSpacingInset='450 400 400 450'
      clickstreamTags={{
        'data-csa-c-type': 'link',
        'data-csa-c-slot-id': `tm-outlier-management-associate-outlier-link-slot`,
        'data-csa-c-tag1': 'Coaching',
        'data-csa-c-tag2': 'Outlier Metrics',
        'data-csa-c-tag3': `Associates Outlier`,
        'data-csa-c-profile-id': requesterUserID,
        'data-csa-c-username': requesterUsername,
        'data-csa-c-is-TM': isTm,
        'data-csa-c-is-OM': isOm,
      }}
    />
  );
};

export const OutlierCardsColumn: React.FC<OutlierCardsColumnProps> = ({
  onFilterChange,
  outlierCategories,
  associatesWithOutliers,
  validCSALogins,
}) => {
  const metricsData = useMemo(
    () => calculateMetricsData(outlierCategories, validCSALogins),
    [outlierCategories, validCSALogins]
  );

  const associatesData = useMemo(
    () => calculateAssociatesData(outlierCategories, validCSALogins),
    [outlierCategories, validCSALogins]
  );

  return (
    <Column>
      <OutlierMetricsCard {...metricsData} onFilterChange={onFilterChange} />
      <AssociatesRequiredReviewsCard
        associatesWithRequiredReviews={
          associatesData.associatesWithRequiredReviews
        }
        onFilterChange={onFilterChange}
      />
    </Column>
  );
};

export default OutlierCardsColumn;
